import { useEffect, useRef, useState } from 'react';
import { useEventListener } from 'usehooks-ts';
import useConversationStore from '@/hooks/conversations/use-conversation-store';

export function useAudioControls(url: string) {
  const audioRef = useRef(new Audio());
  const [isLoading, setLoading] = useState(false);
  const [isPlaying, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [position, setPosition] = useState(0);

  const { isMediaPlaying, setMediaPlaying, clearMediaPlaying } = useConversationStore();

  useEventListener(
    'waiting',
    () => {
      setLoading(true);
    },
    audioRef,
  );

  useEventListener(
    'playing',
    () => {
      setLoading(false);
      setPlaying(true);
      setMediaPlaying(url);
    },
    audioRef,
  );

  useEventListener(
    'timeupdate',
    () => {
      if (!audioRef.current) return;
      const duration = audioRef.current.duration;
      const position = audioRef.current.currentTime;

      setPosition(position);
      setProgress(Number(((position / duration) * 100).toFixed(2)));
    },
    audioRef,
  );

  useEventListener(
    'pause',
    () => {
      setPlaying(false);
      if (isMediaPlaying === url) clearMediaPlaying();
    },
    audioRef,
  );

  useEventListener(
    'ended',
    () => {
      setPlaying(false);
      if (isMediaPlaying === url) clearMediaPlaying();
    },
    audioRef,
  );

  useEffect(() => {
    audioRef.current.preload = 'none';
    audioRef.current.autoplay = false;
    audioRef.current.src = url;
    return () => {
      audioRef.current.pause();
      audioRef.current.src = '';
    };
  }, [url]);

  useEffect(() => {
    if (isMediaPlaying !== url) audioRef.current.pause();
  }, [isMediaPlaying, url]);

  return {
    play: () => audioRef.current.play(),
    pause: () => audioRef.current.pause(),
    toggle: () => {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(console.warn);
      }
    },
    isPlaying,
    isLoading,
    progress,
    position,
  } as const;
}
